import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { CaretLeft, CaretRight, Check } from '@phosphor-icons/react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { TaskChecklist, useTaskDetail } from './TaskChecklist';

const API = process.env.REACT_APP_BACKEND_URL || '';
const HOUR_H = 56;
const MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const WEEKDAYS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];
const WD_SHORT = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

const STATE_STYLE = {
  resolvido: { bg: '#e4f0ef', border: '#bbd6d5', text: '#3f5a58', label: 'Tudo resolvido' },
  por_fazer: { bg: '#f1dd9c', border: '#d7bd7b', text: '#4a3a0c', label: 'Por fazer' },
  atraso: { bg: '#f2a766', border: '#c9772f', text: '#2b1706', label: 'Em atraso' },
  sem_tarefas: { bg: '#f4f4f1', border: '#d9d9d2', text: '#5b6159', label: 'Sem tarefas' },
};

const toMin = (t) => { const [h, m] = (t || '09:00').split(':').map(Number); return h * 60 + (m || 0); };
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parse = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const mondayOf = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const longDay = (s) => { const d = parse(s); return `${WEEKDAYS[(d.getDay() + 6) % 7]}, ${d.getDate()} ${MONTHS[d.getMonth()].slice(0, 3)}`; };

function layoutLanes(items) {
  const sorted = [...items].sort((a, b) => toMin(a.time) - toMin(b.time));
  const lanes = []; const res = new Map();
  let clusterEnd = -1; let clusterItems = [];
  const flush = () => { const n = Math.max(1, ...clusterItems.map(i => res.get(i.appointment_id).lane + 1)); clusterItems.forEach(i => { res.get(i.appointment_id).n = n; }); clusterItems = []; lanes.length = 0; };
  sorted.forEach(it => {
    const s = toMin(it.time); const e = s + (it.duration || 60);
    if (s >= clusterEnd && clusterItems.length) flush();
    let lane = lanes.findIndex(end => end <= s);
    if (lane === -1) { lane = lanes.length; lanes.push(e); } else lanes[lane] = e;
    res.set(it.appointment_id, { lane, n: 1 }); clusterItems.push(it); clusterEnd = Math.max(clusterEnd, e);
  });
  if (clusterItems.length) flush();
  return res;
}

function TaskPanel({ aptId, onClose, onChanged }) {
  const { detail, error, reload } = useTaskDetail({ appointmentId: aptId });
  const reloadAll = async () => { await reload(); onChanged(); };
  const stateKey = detail ? (detail.progress.total === 0 ? 'sem_tarefas' : detail.progress.pending === 0 ? 'resolvido' : 'por_fazer') : 'sem_tarefas';
  return (
    <div data-testid="task-panel">
      {error && <p className="text-sm text-red-600">{error}</p>}
      {detail && (
        <>
          <div className="mb-4">
            <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: STATE_STYLE[stateKey].bg, color: STATE_STYLE[stateKey].text, border: `1px solid ${STATE_STYLE[stateKey].border}` }} data-testid="task-panel-state">
              {stateKey === 'resolvido' ? 'Tudo resolvido' : stateKey === 'sem_tarefas' ? 'Sem tarefas' : `${detail.progress.pending} por fazer`}
            </span>
            <Link to={`/admin/pacientes/${detail.patient_id}`} className="block font-heading text-lg text-brand-text mt-1 underline decoration-[#bbd6d5]" data-testid="task-panel-patient-link">{detail.patient_name}</Link>
            <p className="text-xs text-brand-muted" data-testid="task-panel-when">{longDay(detail.date)} · {detail.time}{detail.end_time ? ` às ${detail.end_time}` : ''}</p>
            <p className="text-xs text-brand-muted" data-testid="task-panel-meta">{[detail.modality, detail.tipo_label, detail.program_label].filter(Boolean).join(' · ')}{detail.nutritionist_name ? ` · ${detail.nutritionist_name}` : ''}</p>
          </div>
          <TaskChecklist detail={detail} reload={reloadAll} tid="panel" />
        </>
      )}
      {!detail && !error && <p className="text-sm text-brand-placeholder">A carregar…</p>}
    </div>
  );
}

export default function WeekView() {
  const { user } = useAuth();
  const teamView = typeof window !== 'undefined' && localStorage.getItem('team_view') === 'true' && user?.role === 'admin';
  const [start, setStart] = useState(ymd(mondayOf(new Date())));
  const [data, setData] = useState(null);
  const [onlyPending, setOnlyPending] = useState(false);
  const [nutri, setNutri] = useState('all');
  const [selected, setSelected] = useState(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => { const h = () => setIsMobile(window.innerWidth < 768); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/api/consult-tasks/week`, { params: { start, nutri_id: teamView && nutri !== 'all' ? nutri : undefined } });
      setData(d);
    } catch { setData({ appointments: [], days: [], counters: { consultas: 0, tarefas_por_fazer: 0, consultas_em_atraso: 0 }, nutritionists: [] }); }
  }, [start, nutri, teamView]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!data?.days?.includes(ymd(new Date()))) return;
    document.querySelector(`[data-testid="week-day-head-${ymd(new Date())}"]`)?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [data]);
  const shift = (n) => { const d = parse(start); d.setDate(d.getDate() + n * 7); setStart(ymd(d)); };
  const apts = data?.appointments || [];
  const weekend = apts.some(a => { const wd = (parse(a.date).getDay() + 6) % 7; return wd >= 5; });
  const days = (data?.days || []).slice(0, weekend ? 7 : 5);
  const [hMin, hMax] = useMemo(() => {
    let lo = 9, hi = 19;
    apts.forEach(a => { lo = Math.min(lo, Math.floor(toMin(a.time) / 60)); hi = Math.max(hi, Math.ceil((toMin(a.time) + a.duration) / 60)); });
    return [lo, hi];
  }, [apts]);
  const today = ymd(new Date());
  const title = useMemo(() => {
    if (!data?.week_start) return '';
    const a = parse(data.week_start); const b = parse(data.week_end);
    return a.getMonth() === b.getMonth() ? `Semana de ${a.getDate()} a ${b.getDate()} de ${MONTHS[b.getMonth()]}` : `Semana de ${a.getDate()} de ${MONTHS[a.getMonth()]} a ${b.getDate()} de ${MONTHS[b.getMonth()]}`;
  }, [data]);
  const isThisWeek = data?.week_start === ymd(mondayOf(new Date()));

  return (
    <div data-testid="week-view">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h2 className="font-heading text-lg text-brand-text mr-auto" data-testid="week-title">{title}</h2>
        <div className="flex items-center gap-1">
          <button onClick={() => shift(-1)} className="w-10 h-10 rounded-xl border border-brand-border bg-white flex items-center justify-center" aria-label="Semana anterior" data-testid="week-prev-btn"><CaretLeft size={16} /></button>
          <button onClick={() => setStart(ymd(mondayOf(new Date())))} disabled={isThisWeek} className="px-3 h-10 rounded-xl border border-brand-border bg-white text-xs disabled:opacity-50" data-testid="week-today-btn">Esta semana</button>
          <button onClick={() => shift(1)} className="w-10 h-10 rounded-xl border border-brand-border bg-white flex items-center justify-center" aria-label="Semana seguinte" data-testid="week-next-btn"><CaretRight size={16} /></button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex gap-1 bg-white border border-brand-border rounded-xl p-1" data-testid="week-filter">
          {[['all', 'Todas'], ['pending', 'Só por fazer']].map(([k, l]) => (
            <button key={k} onClick={() => setOnlyPending(k === 'pending')} className={`text-xs px-3 py-1.5 rounded-lg ${(k === 'pending') === onlyPending ? 'bg-[#657161] text-white' : 'text-brand-muted'}`} data-testid={`week-filter-${k}`}>{l}</button>
          ))}
        </div>
        {teamView && (
          <Select value={nutri} onValueChange={setNutri}>
            <SelectTrigger className="h-9 w-48 text-xs" data-testid="week-nutri-filter"><SelectValue placeholder="Nutricionista" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toda a equipa</SelectItem>
              {(data?.nutritionists || []).filter(n => !/^test/i.test(n.name || '')).map(n => <SelectItem key={n.user_id} value={n.user_id}>{n.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <div className="flex gap-2 ml-auto text-[11px]" data-testid="week-counters">
          <span className="px-2.5 py-1 rounded-full bg-white border border-brand-border whitespace-nowrap" data-testid="counter-consultas"><b>{data?.counters.consultas ?? 0}</b> consultas</span>
          <span className="px-2.5 py-1 rounded-full bg-white border border-brand-border whitespace-nowrap" data-testid="counter-por-fazer"><b>{data?.counters.tarefas_por_fazer ?? 0}</b> tarefas por fazer</span>
          <span className="px-2.5 py-1 rounded-full bg-white border border-brand-border whitespace-nowrap" data-testid="counter-atraso"><b>{data?.counters.consultas_em_atraso ?? 0}</b> em atraso</span>
        </div>
      </div>

      <div className="overflow-x-auto bg-white border border-brand-border rounded-2xl" data-testid="week-grid-scroll">
        <div style={{ minWidth: 640 }}>
          <div className="grid border-b border-brand-border" style={{ gridTemplateColumns: `48px repeat(${days.length}, minmax(0, 1fr))` }}>
            <div />
            {days.map((d, i) => (
              <div key={d} className={`text-center py-2 text-xs ${d === today ? 'bg-[#bbd6d5]/40 font-semibold text-brand-text' : 'text-brand-muted'}`} data-testid={`week-day-head-${d}`}>
                {WD_SHORT[i]} <span className="block text-sm">{parse(d).getDate()}</span>
              </div>
            ))}
          </div>
          <div className="grid relative" style={{ gridTemplateColumns: `48px repeat(${days.length}, minmax(0, 1fr))`, height: (hMax - hMin) * HOUR_H }}>
            <div className="relative">
              {Array.from({ length: hMax - hMin }, (_, i) => <div key={i} className="absolute right-1 text-[10px] text-brand-placeholder" style={{ top: Math.max(i * HOUR_H - 6, 2) }}>{String(hMin + i).padStart(2, '0')}:00</div>)}
            </div>
            {days.map(d => {
              const dayApts = apts.filter(a => a.date === d);
              const lanes = layoutLanes(dayApts);
              return (
                <div key={d} className={`relative border-l border-brand-border/60 ${d === today ? 'bg-[#bbd6d5]/15' : ''}`} data-testid={`week-col-${d}`}>
                  {Array.from({ length: hMax - hMin }, (_, i) => <div key={i} className="absolute inset-x-0 border-t border-brand-border/40" style={{ top: i * HOUR_H }} />)}
                  {dayApts.map(a => {
                    const st = STATE_STYLE[a.state]; const L = lanes.get(a.appointment_id) || { lane: 0, n: 1 };
                    const top = ((toMin(a.time) - hMin * 60) / 60) * HOUR_H;
                    const h = Math.max((a.duration / 60) * HOUR_H - 2, 44);
                    const dim = onlyPending && a.state === 'resolvido';
                    return (
                      <button key={a.appointment_id} onClick={() => setSelected(a.appointment_id)}
                        className="absolute text-left rounded-lg px-1.5 py-1 overflow-hidden transition-opacity hover:shadow-md"
                        style={{ top, height: h, left: `calc(${(L.lane / L.n) * 100}% + 2px)`, width: `calc(${100 / L.n}% - 4px)`, background: st.bg, color: st.text, borderLeft: `4px solid ${st.border}`, border: `1px solid ${st.border}`, borderLeftWidth: 4, opacity: dim ? 0.3 : 1 }}
                        data-testid={`week-block-${a.appointment_id}`} data-state={a.state}>
                        <span className="flex items-center justify-between text-[10px] font-semibold leading-tight">
                          <span>{a.time}</span>
                          <span data-testid={`week-block-count-${a.appointment_id}`}>{a.state === 'resolvido' && <Check size={10} weight="bold" className="inline mr-0.5" />}{a.done}/{a.total}</span>
                        </span>
                        <span className="block text-[11px] font-medium leading-tight truncate">{a.patient_name}</span>
                        <span className="block text-[10px] leading-tight truncate">{a.tipo_short}{a.modality ? ` · ${a.modality}` : ''}</span>
                        {teamView && a.nutritionist_name && <span className="block text-[9px] leading-tight truncate opacity-80" data-testid={`week-block-nutri-${a.appointment_id}`}>{a.nutritionist_name}</span>}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {apts.length === 0 && data && <p className="text-sm text-brand-placeholder text-center py-6" data-testid="week-empty">Sem consultas nesta semana.</p>}

      <div className="flex flex-wrap gap-3 mt-3 text-[11px] text-brand-muted" data-testid="week-legend">
        {['resolvido', 'por_fazer', 'atraso'].map(k => (
          <span key={k} className="inline-flex items-center gap-1.5"><span className="w-4 h-4 rounded" style={{ background: STATE_STYLE[k].bg, border: `1px solid ${STATE_STYLE[k].border}`, borderLeftWidth: 4 }} />{STATE_STYLE[k].label}{k === 'atraso' ? ' (consulta já passou)' : ''}</span>
        ))}
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <SheetContent side={isMobile ? 'bottom' : 'right'} className={isMobile ? 'max-h-[88vh] overflow-y-auto rounded-t-3xl' : 'w-full sm:max-w-md overflow-y-auto'} data-testid="task-panel-sheet">
          <SheetHeader><SheetTitle className="font-heading text-sm text-brand-muted">Tarefas da consulta</SheetTitle></SheetHeader>
          <div className="mt-3">{selected && <TaskPanel key={selected} aptId={selected} onClose={() => setSelected(null)} onChanged={load} />}</div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
